import React from 'react';
import cx from 'classnames';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MUICheckbox, { CheckboxProps as MUICheckboxProps } from '@material-ui/core/Checkbox';

import { makeStyles, useTheme } from '../styles';

type CheckboxProps = {
  label: React.ReactNode;
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
  checked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  error?: boolean;
  name?: string;
  value?: string;
  edge?: false | 'start' | 'end';
  checkBoxColor?: string;
  /** Focusing on the checkbox can inhibit keyboard shortcuts */
  unfocusable?: boolean;
  tabIndex?: number;
  classes?: { label?: string; checkboxRoot?: string };
} & Pick<MUICheckboxProps, 'onChange' | 'onFocus' | 'className' | 'style' | 'icon' | 'checkedIcon'>;

const useStyles = makeStyles({
  root: {
    marginLeft: 0,
    marginRight: 0,
  },
  label: {
    fontSize: 14,
    fontWeight: 400,
    marginLeft: 0,
    marginRight: 0,
  },
  CheckboxIconUnchecked: {
    stroke: '#777777',
    '&:hover': {
      stroke: '#808080',
    },
  },
});

const Selected = ({ fill, disabled }: { fill: string; disabled?: boolean }) => {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="14" height="14" rx="2" fill={disabled ? '#777777' : fill} />
      <path
        d="M6.46053 11.4387L3.34053 8.3187C3.15309 8.13126 3.15309 7.82734 3.34053 7.63988L4.01934 6.96105C4.20678 6.77359 4.51072 6.77359 4.69817 6.96105L6.79995 9.06282L11.3017 4.56105C11.4892 4.37361 11.7931 4.37361 11.9806 4.56105L12.6594 5.23988C12.8468 5.42732 12.8468 5.73124 12.6594 5.9187L7.13936 11.4387C6.9519 11.6262 6.64798 11.6262 6.46053 11.4387Z"
        fill="white"
      />
    </svg>
  );
};

const Unselected = () => {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1.5" y="1.5" width="13" height="13" rx="1.5" fill="#F8F8F8" stroke="#777777" />
    </svg>
  );
};

const Indeterminate = ({ fill, disabled }: { fill: string; disabled?: boolean }) => {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="14" height="14" rx="2" fill={disabled ? '#CCCCCC' : fill} />
      <rect x="4" y="7" width="8" height="2" rx="0.5" fill="white" />
    </svg>
  );
};

const Error = ({ fill, disabled }: { fill: string; disabled?: boolean }) => {
  return (
    <svg width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="10" height="10" rx="2" fill={disabled ? '#CCCCCC' : fill} />
      <path
        d="M8.31342 9.43027C8.1818 9.5619 7.96641 9.56189 7.83478 9.43027L6 7.59548L4.16522 9.43027C4.03359 9.5619 3.8182 9.5619 3.68658 9.43027L2.56975 8.31345C2.43812 8.18182 2.43813 7.96643 2.56975 7.83481L4.40454 6.00002L2.56975 4.16524C2.43813 4.03361 2.43813 3.81822 2.56975 3.6866L3.68658 2.56977C3.8182 2.43815 4.03359 2.43815 4.16522 2.56977L6 4.40456L7.83478 2.56977C7.96641 2.43815 8.1818 2.43815 8.31342 2.56977L9.43025 3.6866C9.56187 3.81822 9.56187 4.03361 9.43025 4.16524L7.59546 6.00002L9.43025 7.83481C9.56187 7.96643 9.56187 8.18182 9.43025 8.31344L8.31342 9.43027Z"
        fill="white"
      />
    </svg>
  );
};

const blur: React.FocusEventHandler<HTMLButtonElement> = e => e.target.blur();

export function Checkbox({
  label,
  labelPlacement,
  indeterminate,
  error,
  checked,
  disabled,
  onChange,
  onFocus,
  className,
  style,
  name,
  value,
  classes,
  edge = false,
  unfocusable = false,
  checkedIcon,
  icon,
  checkBoxColor,
  tabIndex,
}: CheckboxProps) {
  const styles = useStyles();
  const theme = useTheme();

  return (
    <FormControlLabel
      name={name}
      label={label}
      labelPlacement={labelPlacement || 'end'}
      style={style}
      classes={{
        label: cx(styles.label, classes?.label),
        root: cx(styles.root, className),
      }}
      control={
        <MUICheckbox
          classes={{ root: classes?.checkboxRoot }}
          disableRipple
          color="primary"
          checkedIcon={
            checkedIcon ||
            (error ? (
              <Error disabled={disabled} fill="red" />
            ) : (
              <Selected disabled={disabled} fill={checkBoxColor || theme.colors.primary1} />
            ))
          }
          indeterminateIcon={
            <Indeterminate disabled={disabled} fill={checkBoxColor || theme.colors.primary1} />
          }
          icon={icon || <Unselected />}
          checked={checked}
          indeterminate={indeterminate}
          onChange={onChange}
          value={value}
          onFocus={unfocusable ? blur : onFocus}
          edge={edge}
          tabIndex={tabIndex}
        />
      }
      disabled={disabled}
    />
  );
}

export default Checkbox;
